import { useState } from "react";
import type { AccountExportOutput } from "@panelgenie/contracts";
import {
  Banner,
  Button,
  Card,
  Heading,
  Modal,
} from "@panelgenie/shared-ui";

/**
 * Settings → Account export affordance.
 *
 * The user taps "Export my data". The component renders a
 * confirmation modal carrying the export SLA copy returned from the
 * server. On confirm, the parent route invokes `onExport()`; the
 * parent owns the tRPC call + the blob-download seam. The component
 * reports progress + outcome through the `state` prop.
 *
 * Pure presentation — no fetch logic, no seam wiring.
 */
export type AccountExportState =
  | { readonly kind: "idle" }
  | { readonly kind: "exporting" }
  | { readonly kind: "ready"; readonly result: AccountExportOutput }
  | { readonly kind: "error"; readonly message: string };

export type AccountExportPanelProps = {
  readonly state: AccountExportState;
  readonly onExport: () => void;
  readonly slaCopy: string;
};

export function AccountExportPanel({
  state,
  onExport,
  slaCopy,
}: AccountExportPanelProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isBusy = state.kind === "exporting";
  return (
    <Card aria-label="account-export">
      <Card.Header>
        <Heading level="h2">Export my data</Heading>
      </Card.Header>
      <Card.Body className="space-y-3">
        <p className="text-base text-fg">
          Download a JSON archive of every site, room, panel, device, and
          warning on your account, plus the photo bundle. This is a
          one-click, all-tier-included data ownership affordance.
        </p>
        <p className="text-sm text-mute">{slaCopy}</p>
        <Button
          onClick={() => setIsModalOpen(true)}
          disabled={isBusy}
          loading={isBusy}
          aria-label="export-my-data"
        >
          {isBusy ? "Preparing your archive…" : "Export my data"}
        </Button>

        {state.kind === "error" ? (
          <Banner variant="danger" role="alert">
            {state.message}
          </Banner>
        ) : null}

        {state.kind === "ready" ? (
          <Banner variant="success" role="status">
            Archive downloaded. {state.result.siteCount} site
            {state.result.siteCount === 1 ? "" : "s"},{" "}
            {state.result.photoCount} photo
            {state.result.photoCount === 1 ? "" : "s"}.
          </Banner>
        ) : null}
      </Card.Body>

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-label="confirm-export-dialog"
        title="Export your data?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button
              aria-label="confirm-export"
              onClick={() => {
                setIsModalOpen(false);
                onExport();
              }}
            >
              Yes, export
            </Button>
          </>
        }
      >
        <p className="text-base text-fg">
          We&apos;ll prepare a download with every site, room, panel,
          device, and warning on your account, plus the photo bundle.
        </p>
        <p className="mt-3 text-sm text-mute">{slaCopy}</p>
      </Modal>
    </Card>
  );
}
